import { CloudFunctionHttpError } from 'services/cloud-functions-errors';

/** What an email sign-in attempt ended with, when it did not throw. */
export enum EmailSignInOutcome {
  /** the password was checked by Firebase and the user is signed in */
  SignedIn = 'SignedIn',
  /** the account has another sign-in method and the user chose to use that instead */
  AlternativeChosen = 'AlternativeChosen',
}

/** What a failed sign-in-methods lookup is reported with. */
export interface SignInMethodsLookupFailure {
  code?: string;
  message?: string;
}

/**
 * Lookup failures caused by the user or their connection, not by Google. The sign-in or
 * sign-up call that follows fails with the same code and shows it to the user, so reporting
 * them too would bury the Google-side failures the report exists to count.
 */
const LOOKUP_FAILURES_THE_NEXT_STEP_SHOWS = new Set([
  'auth/invalid-email',
  'auth/network-request-failed',
  'auth/too-many-requests',
]);

/**
 * Ask which sign-in methods an email has (Firebase `fetchSignInMethodsForEmail`).
 * The answer only decides whether to offer Google/Apple instead of the password, so a
 * failed lookup is read as "no methods known" instead of blocking sign-in, and reported
 * unless the next step will show the same failure. Firebase still checks the password on
 * the server (#5472).
 */
export async function lookUpSignInMethodsAsync(
  lookup: () => Promise<string[]>,
  reportFailure: (failure: SignInMethodsLookupFailure) => void,
): Promise<string[]> {
  try {
    return await lookup();
  } catch (error) {
    if (!LOOKUP_FAILURES_THE_NEXT_STEP_SHOWS.has(error?.code)) {
      reportFailure({ code: error?.code, message: error?.message });
    }
    return [];
  }
}

export interface EmailSignInSteps {
  /** the raw lookup; may throw */
  lookUpMethods: () => Promise<string[]>;
  reportLookupFailure: (failure: SignInMethodsLookupFailure) => void;
  /** shows the "use Google/Apple instead?" alert; resolves true if the user took the alternative */
  offerAlternativeSignIn: (methods: string[]) => Promise<boolean>;
  /** sends the email and password to Firebase, which checks them; throws on a bad password */
  signIn: () => Promise<unknown>;
}

/** The order of steps behind the login page's email sign-in button. */
export async function signInWithEmailSequenceAsync(steps: EmailSignInSteps): Promise<EmailSignInOutcome> {
  const methods = await lookUpSignInMethodsAsync(steps.lookUpMethods, steps.reportLookupFailure);
  console.log('Login methods available: ', JSON.stringify(methods));
  // if the account has another method, offer it first; the user may still decline and use the password
  if (methods.some(m => m != 'password') && await steps.offerAlternativeSignIn(methods)) {
    return EmailSignInOutcome.AlternativeChosen;
  }
  await steps.signIn();
  return EmailSignInOutcome.SignedIn;
}

/** The steps behind the login page's answer to `auth/account-exists-with-different-credential`. */
export interface ExistingAccountSteps {
  /** the raw lookup of the email's sign-in methods; may throw */
  lookUpMethods: () => Promise<string[]>;
  reportLookupFailure: (failure: SignInMethodsLookupFailure) => void;
  /** starts the "use Google/Apple instead?" offer; not awaited, as before #5191 */
  offerAlternativeSignIn: (methods: string[]) => void;
  /** shows the original sign-in error to the person */
  showSignInError: () => Promise<void>;
}

/**
 * A social sign-in found the email already has an account with another method. Offer that
 * method; when there is none to offer (the lookup failed, or found nothing), show the sign-in
 * error. Before #5191 that last case showed nothing, and the button just stopped.
 *
 * Every lookup failure is reported here, unlike in `lookUpSignInMethodsAsync`: that one skips the
 * failures a following sign-in call will show, and on this path no sign-in call follows.
 */
export async function answerExistingAccountAsync(steps: ExistingAccountSteps): Promise<void> {
  let methods: string[] = [];
  try {
    methods = await steps.lookUpMethods();
  } catch (error) {
    steps.reportLookupFailure({ code: error?.code, message: error?.message });
  }
  if (methods.length > 0) {
    steps.offerAlternativeSignIn(methods);
  } else {
    await steps.showSignInError();
  }
}

/**
 * Whether a failed `signInByLink` call means the server refused the address as malformed.
 * The server answers Firebase's `auth/invalid-email` with a 400 (fitness-app#1727), and the
 * client's error carries only the status, so the login page shows its inline
 * invalid-address message for exactly this answer rather than the contact-support alert.
 */
export function isSignInLinkAddressRefused(error: unknown): boolean {
  return error instanceof CloudFunctionHttpError && error.httpStatus === 400;
}
